/**
 * Live word counter for the Submit-a-Resource abstract (PATH-15).
 *
 * Advisory only. The field says "100–500 words" and nothing measured it, so a member
 * had no way to tell where they were. This counts as you type and nudges when you fall
 * outside the range — it never blocks a submission. See
 * ResourceSubmissionProvider::ABSTRACT_MIN_WORDS for why the range is a hint and not a
 * rule (short version: no published resource is inside it).
 *
 * Fields opt in with the `path-wordcount` marker class, which the provider puts on the
 * field; the thresholds arrive on `window.pathAbstractWords`. Neither a form id nor a
 * field id appears here.
 */

const MARKER = 'path-wordcount';
const ANNOUNCE_DELAY = 600;

/** Fallback if the inline config is missing — keep in step with the PHP constants. */
const DEFAULTS = { min: 100, max: 500 };

/**
 * Words in a string. Matches PHP's str_word_count closely enough for a hint: runs of
 * non-whitespace, so "state-by-state" is one word and stray punctuation is not.
 *
 * @param {string} value
 * @return {number} How many words the value contains.
 */
export function countWords( value ) {
	const empty = value === null || value === undefined;
	const trimmed = String( empty ? '' : value ).trim();

	if ( trimmed === '' ) {
		return 0;
	}

	return trimmed.split( /\s+/ ).length;
}

/**
 * Which of the three states a count is in, and what to say about it.
 *
 * The message always carries the state IN WORDS. Colour alone would fail WCAG 1.4.1,
 * and this is the same pass that is fixing the site's other contrast problems.
 *
 * @param {number}                     count
 * @param {{min: number, max: number}} range
 * @return {{state: string, message: string}} State name, and the line to show.
 */
export function describe( count, range ) {
	const { min, max } = range;
	const words = `${ count } ${ count === 1 ? 'word' : 'words' }`;

	if ( count === 0 ) {
		return { state: 'empty', message: `Aim for ${ min }–${ max } words.` };
	}

	if ( count < min ) {
		return { state: 'short', message: `${ words } — aim for at least ${ min }.` };
	}

	if ( count > max ) {
		return { state: 'long', message: `${ words } — try trimming to ${ max } or fewer.` };
	}

	return { state: 'ok', message: `${ words } — looks good.` };
}

/**
 * Read the thresholds, falling back if the inline config never printed.
 *
 * @return {{min: number, max: number}} The advisory word range.
 */
function readRange() {
	const config = window.pathAbstractWords;

	if ( ! config ) {
		return DEFAULTS;
	}

	const min = parseInt( config.min, 10 );
	const max = parseInt( config.max, 10 );

	return {
		min: Number.isFinite( min ) ? min : DEFAULTS.min,
		max: Number.isFinite( max ) ? max : DEFAULTS.max,
	};
}

/**
 * Attach a counter to one textarea.
 *
 * @param {HTMLTextAreaElement}        field
 * @param {{min: number, max: number}} range
 * @return {void}
 */
function attach( field, range ) {
	if ( field.dataset.wordCountReady === 'true' ) {
		return;
	}

	field.dataset.wordCountReady = 'true';

	const hint = document.createElement( 'p' );
	hint.className = 'path-word-count';
	hint.id = `${ field.id || 'abstract' }-word-count`;

	// role=status announces changes politely; the debounce below is what keeps it from
	// speaking on every keystroke.
	hint.setAttribute( 'role', 'status' );

	// Point the field at the hint WITHOUT dropping GF's own description, which is
	// already wired up here and carries the "100-500 words" guidance.
	const described = ( field.getAttribute( 'aria-describedby' ) || '' )
		.split( /\s+/ )
		.filter( Boolean );
	described.push( hint.id );
	field.setAttribute( 'aria-describedby', described.join( ' ' ) );

	// AFTER the field's own description, not straight after the textarea. GF renders
	// the description below the input, so inserting at the textarea puts the live count
	// above the static "100-500 words" guidance — the running total appearing before
	// the target it is counting towards.
	const container = field.closest( '.gfield' );
	const description = container ? container.querySelector( '.gfield_description' ) : null;

	( description || field ).insertAdjacentElement( 'afterend', hint );

	// Paint immediately, but let the announcement lag: role=status re-reads on every
	// text change, and updating it per keystroke makes a screen reader unusable.
	const announce = text => {
		hint.textContent = text;
	};
	const announceLater =
		typeof window.debounce === 'function'
			? window.debounce( announce, ANNOUNCE_DELAY )
			: announce;

	let painted = false;

	const update = () => {
		const { state, message } = describe( countWords( field.value ), range );

		hint.dataset.state = state;

		if ( ! painted ) {
			// First paint is not a change anyone is listening for.
			painted = true;
			hint.textContent = message;
			return;
		}

		announceLater( message );
	};

	field.addEventListener( 'input', update );
	update();
}

/**
 * Wire every marked field. Safe to call again — GF redraws the form on AJAX
 * validation, and `attach` no-ops on a field it already owns.
 *
 * @return {void}
 */
export function initWordCount() {
	const range = readRange();

	document.querySelectorAll( `.${ MARKER } textarea` ).forEach( field => attach( field, range ) );
}

document.addEventListener( 'DOMContentLoaded', initWordCount );

// GF re-renders the form after an AJAX submit or a validation failure, which throws
// away the injected hint. jQuery is GF's own event bus, so only bind when it is there.
if ( typeof window.jQuery === 'function' ) {
	window.jQuery( document ).on( 'gform_post_render', initWordCount );
}
